import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';
import './AssessmentTokenUI_NEW.css';
import logger from '../utils/logger';

const AssessmentTokenUI = ({ values, onValuesChange, isOferta = true, onComplete, assessmentType = '', context = 'offers' }) => {
  const { language, metaCapitals, t, changeLanguage } = useLanguage();
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [showSummary, setShowSummary] = useState(false);
  const [capitalContent, setCapitalContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const chartContainerRef = useRef(null);

  const capitalColors = ['#e53e3e', '#dd6b20', '#38a169', '#319795', '#48bb78', '#3182ce', '#805ad5', '#d69e2e', '#c53030', '#718096'];
  const sliderMarks = [50, 35, 25, 15, 10, 5, 0];
  const totalTokens = Object.values(values).reduce((sum, val) => sum + val, 0);
  const currentCapital = metaCapitals[currentTabIndex] || {};
  
  function getCapitalKey(name) {
    const map = {
      'Capital Físico': 'capital_fisico', 'Physical Capital': 'capital_fisico', 'Physical': 'capital_fisico', 'Físico': 'capital_fisico',
      'Capital Humano': 'capital_humano', 'Human Capital': 'capital_humano', 'Human': 'capital_humano', 'Humano': 'capital_humano',
      'Herramientas': 'capital_manufacturado', 'Tools': 'capital_manufacturado', 'Manufactured Capital': 'capital_manufacturado', 'Manufactured': 'capital_manufacturado', 'Manufacturado': 'capital_manufacturado',
      'Capital Financiero': 'capital_financiero', 'Financial Capital': 'capital_financiero', 'Financial': 'capital_financiero', 'Financiero': 'capital_financiero',
      'Capital Natural': 'capital_natural', 'Natural Capital': 'capital_natural', 'Natural': 'capital_natural',
      'Capital Cultural': 'capital_cultural', 'Cultural Capital': 'capital_cultural', 'Cultural': 'capital_cultural',
      'Capital Social': 'capital_social', 'Social Capital': 'capital_social', 'Social': 'capital_social',
      'Capital Conocimiento': 'capital_conocimiento', 'Knowledge Capital': 'capital_conocimiento', 'Knowledge': 'capital_conocimiento', 'Conocimiento': 'capital_conocimiento',
      'Capital Psicológico': 'capital_psicologico', 'Psychological Capital': 'capital_psicologico', 'Psychological': 'capital_psicologico', 'Psicológico': 'capital_psicologico',
      'Propósito': 'capital_espiritual', 'Purpose': 'capital_espiritual', 'Spiritual Capital': 'capital_espiritual', 'Spiritual': 'capital_espiritual', 'Espiritual': 'capital_espiritual'
    };
    return map[name] || `capital_${name.toLowerCase().replace('capital ', '').replace(' ', '_')}`;
  }

  // Determine API context based on assessment type and section
  const getApiContext = () => {
    // assessmentType examples: "individual_complete", "candidate_complete", "company_complete"
    // context: "offers" or "needs"
    
    const isCompany = assessmentType.includes('company');
    const isCandidate = assessmentType.includes('candidate');
    const isEmployee = assessmentType.includes('individual') || assessmentType.includes('employee');
    
    if (isCompany) {
      return context === 'offers' ? 'company_offers' : 'company_needs';
    } else if (isCandidate) {
      return context === 'offers' ? 'candidate_offers' : 'candidate_needs';
    } else {
      // Default to employee
      return context === 'offers' ? 'employee_offers' : 'employee_needs';
    }
  };

  // Load capital content from API
  useEffect(() => {
    const loadCapitalContent = async () => {
      if (currentTabIndex < 0 || currentTabIndex >= 10) return;
      
      setLoadingContent(true);
      const capitalId = currentTabIndex + 1; // 1-based index
      const apiContext = getApiContext();
      
      try {
        const response = await axios.get(`/meta-capitals/${capitalId}/${apiContext}/${language}`);
        if (response.data.success) {
          setCapitalContent(response.data.data);
        }
      } catch (error) {
        logger.error('Error loading capital content:', error);
        // Fallback to existing metaCapitals data
        setCapitalContent(null);
      } finally {
        setLoadingContent(false);
      }
    };

    loadCapitalContent();
  }, [currentTabIndex, language, assessmentType, context]);

  const capitalKey = getCapitalKey(currentCapital.name);
  const currentValue = values[capitalKey] || 0;
  const currentColor = capitalColors[currentTabIndex] || '#667eea';

  const handleValueChange = (newValue) => {
    const validValue = Math.max(0, Math.min(100, parseInt(newValue) || 0));
    onValuesChange({ ...values, [capitalKey]: validValue });
  };

  const distributeEvenly = () => {
    const newValues = {};
    metaCapitals.forEach(capital => { newValues[getCapitalKey(capital.name)] = 10; });
    onValuesChange(newValues);
    toast.success(t('assessment_ui.distribute_equal', 'Distribuido equitativamente'));
  };

  const clearAll = () => {
    if (!window.confirm(t('assessment_ui.reset_confirmation', '¿Estás seguro?'))) return;
    const newValues = {};
    metaCapitals.forEach(capital => { newValues[getCapitalKey(capital.name)] = 0; });
    onValuesChange(newValues);
    toast.success(t('assessment_ui.reset_all', 'Reseteado'));
  };

  const goToNextEmpty = () => {
    for (let i = 1; i <= metaCapitals.length; i++) {
      const nextIndex = (currentTabIndex + i) % metaCapitals.length;
      const key = getCapitalKey(metaCapitals[nextIndex].name);
      if (values[key] === 0) { setCurrentTabIndex(nextIndex); return; }
    }
    toast.info(t('assessment_ui.capitals_assigned', 'Todos tienen tokens'));
  };

  const isValid = () => {
    if (totalTokens !== 100) return false;
    return metaCapitals.every(capital => values[getCapitalKey(capital.name)] >= 1);
  };

  const handleSubmit = () => {
    if (!isValid()) {
      toast.error(totalTokens !== 100 ? t('assessment_ui.status_need_more', 'Total debe ser 100') : t('assessment_ui.min_tokens_warning', 'Cada capital necesita 1 token'));
      return;
    }
    if (onComplete) onComplete();
  };

  useEffect(() => {
    if (!chartContainerRef.current || metaCapitals.length === 0) return;
    const svg = chartContainerRef.current.querySelector('svg');
    if (!svg) return;
    // SEC-001 P1.1: Safe SVG clear
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    const container = chartContainerRef.current;
    container.querySelectorAll('.chart-label').forEach(label => label.remove());

    // SIEMPRE dividir la gráfica en 10 partes iguales (36° cada una)
    // Cada slice tiene un contorno y un fill proporcional al valor
    metaCapitals.forEach((capital, i) => {
      const value = values[getCapitalKey(capital.name)] || 0;
      const start = (i * 360) / metaCapitals.length - 90; // -90 para empezar arriba
      const end = ((i + 1) * 360) / metaCapitals.length - 90;
      
      // Dibujar contorno del slice (siempre con opacidad baja)
      svg.appendChild(createSlice(start, end, capitalColors[i], 0.12));
      
      // Si tiene valor, dibujar fill proporcional con mejor visibilidad
      if (value > 0) {
        const fillPercentage = value / 100; // Porcentaje del valor (0-1)
        svg.appendChild(createPartialFill(start, end, capitalColors[i], fillPercentage));
        
        // Agregar un borde interno para hacer más visible el progreso
        svg.appendChild(createProgressBorder(start, end, capitalColors[i], fillPercentage));
      }
      
      // Labels ahora están a la izquierda del pie chart, no flotantes
      // addLabel(capital, (start + end) / 2, value, container, capitalColors[i]);
    });
  }, [values, metaCapitals]);

  const createSlice = (start, end, color, opacity = 1) => {
    const r = 100, sRad = (start * Math.PI) / 180, eRad = (end * Math.PI) / 180;
    const x1 = r * Math.cos(sRad), y1 = r * Math.sin(sRad), x2 = r * Math.cos(eRad), y2 = r * Math.sin(eRad);
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', `M 0 0 L ${x1} ${y1} A ${r} ${r} 0 ${end - start > 180 ? 1 : 0} 1 ${x2} ${y2} Z`);
    path.setAttribute('fill', color);
    path.setAttribute('opacity', opacity);
    path.setAttribute('stroke', 'white');
    path.setAttribute('stroke-width', '2');
    return path;
  };

  const createPartialFill = (start, end, color, percentage) => {
    // Escalar el relleno para que sea más visible
    // Rango: 0-100% de valor → 30-100% de visualización
    // Esto hace que incluso valores pequeños sean claramente visibles
    const minVisible = 0.3; // Mínimo 30% visible para valores > 0
    const scaledPercentage = minVisible + (percentage * (1 - minVisible));
    const r = 100 * scaledPercentage;
    
    const sRad = (start * Math.PI) / 180, eRad = (end * Math.PI) / 180;
    const x1 = r * Math.cos(sRad), y1 = r * Math.sin(sRad), x2 = r * Math.cos(eRad), y2 = r * Math.sin(eRad);
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', `M 0 0 L ${x1} ${y1} A ${r} ${r} 0 ${end - start > 180 ? 1 : 0} 1 ${x2} ${y2} Z`);
    path.setAttribute('fill', color);
    path.setAttribute('opacity', 0.85);
    path.setAttribute('stroke', 'white');
    path.setAttribute('stroke-width', '1.5');
    return path;
  };

  const createProgressBorder = (start, end, color, percentage) => {
    // Crear un borde/línea visible en el límite del progreso
    const minVisible = 0.3;
    const scaledPercentage = minVisible + (percentage * (1 - minVisible));
    const r = 100 * scaledPercentage;
    
    const sRad = (start * Math.PI) / 180, eRad = (end * Math.PI) / 180;
    const x1 = r * Math.cos(sRad), y1 = r * Math.sin(sRad), x2 = r * Math.cos(eRad), y2 = r * Math.sin(eRad);
    
    // Crear un arco que marca el borde del progreso
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', `M ${x1} ${y1} A ${r} ${r} 0 ${end - start > 180 ? 1 : 0} 1 ${x2} ${y2}`);
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', color);
    path.setAttribute('stroke-width', '1');
    path.setAttribute('opacity', '1');
    return path;
  };

  const addLabel = (capital, angleDeg, value, container, color) => {
    const r = 120, angleRad = (angleDeg * Math.PI) / 180;
    const x = r * Math.cos(angleRad), y = r * Math.sin(angleRad);
    const label = document.createElement('div');
    label.className = 'chart-label';
    label.style.color = color;
    label.style.borderColor = color;
    const cleanName = capital.name.replace(/Capital\s*/gi, '').trim();
    // SEC-001 P1.1: Safe DOM — no innerHTML
    const strong = document.createElement('strong');
    strong.textContent = `${cleanName}: ${value}`;
    label.appendChild(strong);
    const rect = container.getBoundingClientRect();
    label.style.left = (rect.width / 2 + x) + 'px';
    label.style.top = (rect.height / 2 + y) + 'px';
    container.appendChild(label);
  };

  if (showSummary) {
    return (
      <div className="space-y-6 p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('assessment_ui.summary_title', 'Resumen')}</h2>
          <p className="text-gray-600">{t('assessment_ui.summary_description', 'Revisa tu distribución')}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {metaCapitals.map((capital, i) => {
            const value = values[getCapitalKey(capital.name)] || 0;
            return (
              <div key={i} className="bg-white p-4 rounded-lg border" style={{ borderColor: capitalColors[i] }}>
                <div className="text-3xl mb-2 text-center">{capital.icon}</div>
                <div className="text-sm font-medium text-center mb-1">{capital.name.replace(/Capital\s*/gi, '').trim()}</div>
                <div className="text-2xl font-bold text-center" style={{ color: capitalColors[i] }}>{value}</div>
              </div>
            );
          })}
        </div>
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="font-medium">{t('assessment_ui.total_assigned', 'Total')}:</span>
            <span className={`text-2xl font-bold ${totalTokens === 100 ? 'text-green-600' : 'text-red-600'}`}>{totalTokens}/100</span>
          </div>
        </div>
        <div className="flex justify-center space-x-4">
          <Button variant="outline" onClick={() => setShowSummary(false)}>{t('assessment_ui.back_to_edit', 'Volver')}</Button>
          <Button onClick={handleSubmit} disabled={!isValid()}>{t('assessment_ui.submit_assessment', 'Enviar')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-container-new">
      {/* Header */}
      <div className="assessment-header">
        <div className="header-content">
          <h1>
            <span>🎯</span>
            {t('assessment_ui.header_title', 'Not HR Assessment')}
          </h1>
          <p>{isOferta ? t('assessment_ui.header_subtitle_offers', 'Lo que OFREZCO') : t('assessment_ui.header_subtitle_needs', 'Lo que NECESITO')}</p>
        </div>
        <button 
          className="language-toggle"
          onClick={() => changeLanguage(language === 'es' ? 'en' : 'es')}
        >
          🌐 {language === 'es' ? 'EN' : 'ES'}
        </button>
      </div>

      {/* 3 Column Layout */}
      <div className="three-column-layout">
        {/* LEFT COLUMN: Pie Chart + Slider */}
        <div className="left-column">
          {/* Pie Chart */}
          <div className="pie-chart-section">
            {/* Labels on the left */}
            <div className="chart-labels-left">
              {metaCapitals.map((capital, i) => {
                const value = values[getCapitalKey(capital.name)] || 0;
                return (
                  <div key={i} className="chart-label-item">
                    <div className="label-color-dot" style={{ background: capitalColors[i] }}></div>
                    <span className="label-name">{capital.name?.replace(/Capital\s*/gi, '').trim()}</span>
                    <span className="label-value">{value}</span>
                  </div>
                );
              })}
            </div>
            
            {/* Pie chart on the right */}
            <div ref={chartContainerRef} className="chart-container-new">
              <svg viewBox="-120 -120 240 240"></svg>
            </div>
          </div>

          {/* Total Assigned */}
          <div className="total-display-new">
            <h3>{t('assessment_ui.total_assigned', 'Total')}</h3>
            <div className={`total-value ${totalTokens === 100 ? 'complete' : 'incomplete'}`}>{totalTokens}</div>
            <div className="status-message">
              {totalTokens === 100 ? `✅ ${t('assessment_ui.status_perfect', 'Perfecto')}` : totalTokens < 100 ? `⚠️ ${100 - totalTokens}` : `❌ +${totalTokens - 100}`}
            </div>
          </div>

          {/* Horizontal Slider with Input */}
          <div className="slider-section-new">
            <div className="slider-header-row">
              <div className="slider-title-text">
                {t('assessment_ui.assign_tokens', 'Asigna tus tokens')}
              </div>
              <input 
                type="number" 
                min="0" 
                max="100" 
                value={currentValue} 
                onChange={(e) => handleValueChange(e.target.value)} 
                className="slider-input-new" 
                style={{ borderColor: currentColor }} 
              />
              {/* Level Indicator */}
              {currentValue > 0 && (
                <div className={`level-indicator level-${
                  currentValue >= 20 ? 'high' : 
                  currentValue >= 10 ? 'medium' : 
                  'low'
                }`}>
                  {currentValue >= 20 
                    ? t('assessment_ui.example_high', 'ALTO')
                    : currentValue >= 10 
                    ? t('assessment_ui.example_medium', 'MEDIO')
                    : t('assessment_ui.example_low', 'BAJO')
                  }
                </div>
              )}
            </div>
            <div className="slider-controls">
              <div className="horizontal-slider-wrapper">
                <input
                  type="range"
                  min="0"
                  max="50"
                  value={currentValue}
                  onChange={(e) => handleValueChange(e.target.value)}
                  className="horizontal-slider"
                  style={{
                    background: `linear-gradient(to right, ${currentColor} 0%, ${currentColor} ${(currentValue / 50) * 100}%, #e2e8f0 ${(currentValue / 50) * 100}%, #e2e8f0 100%)`
                  }}
                />
                <div className="slider-marks-horizontal">
                  {[0, 10, 15, 25, 35, 50].map(mark => (
                    <span key={mark} className="mark-label">{mark}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CENTER COLUMN: Capitals List */}
        <div className="center-column">
          <div className="capitals-list">
            {metaCapitals.map((capital, i) => {
              const value = values[getCapitalKey(capital.name)] || 0;
              const isActive = i === currentTabIndex;
              return (
                <div
                  key={i}
                  className={`capital-list-item ${isActive ? 'active' : ''}`}
                  onClick={() => setCurrentTabIndex(i)}
                  style={{ borderLeftColor: isActive ? capitalColors[i] : 'transparent' }}
                >
                  <span className="capital-name">{capital.name?.replace(/Capital\s*/gi, '').trim().toUpperCase()}</span>
                  <span className="capital-value" style={{ color: capitalColors[i] }}>{value}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT COLUMN: Capital Details */}
        <div className="right-column">
          {loadingContent ? (
            <div className="loading-content">Cargando...</div>
          ) : (
            <div className="capital-details">
              {/* Title with emoji */}
              <div className="detail-header">
                <h2 style={{ color: currentColor }}>
                  {capitalContent?.emoji || currentCapital.icon} {capitalContent?.name || currentCapital.name?.replace(/Capital\s*/gi, '').trim()}
                </h2>
              </div>

              {/* Definition */}
              {capitalContent?.definition && (
                <div className="detail-definition">{capitalContent.definition}</div>
              )}

              {/* Context */}
              {capitalContent?.context_description && (
                <div className="detail-context">
                  <strong>{t('assessment_ui.context_label', 'Contexto:')}</strong> {capitalContent.context_description}
                </div>
              )}

              {/* Bullets */}
              {capitalContent?.bullets && capitalContent.bullets.length > 0 && (
                <ul className="detail-bullets">
                  {capitalContent.bullets.map((bullet, idx) => (
                    <li key={idx}>{bullet}</li>
                  ))}
                </ul>
              )}

              {/* Key Question with Tooltip */}
              {capitalContent?.key_question && (
                <div className="detail-question">
                  <div className="question-title">
                    <strong>{t('assessment_ui.key_question', 'Pregunta clave')}</strong>
                    {capitalContent?.examples && (
                      <div className="examples-tooltip">
                        <span className="examples-label">{t('assessment_ui.examples_label', 'Ejemplos:')}</span>
                        <div className="examples-tooltip-icon">?</div>
                        <div className="examples-tooltip-content">
                          <div className="example-high">
                            <strong>{t('assessment_ui.example_high', 'ALTO')} ({capitalContent.examples.high.tokens} {t('assessment_ui.tokens', 'tokens')}):</strong>
                            <p>{capitalContent.examples.high.description}</p>
                          </div>
                          {capitalContent.examples.medium && (
                            <div className="example-medium">
                              <strong>MEDIO ({capitalContent.examples.medium.tokens} {t('assessment_ui.tokens', 'tokens')}):</strong>
                              <p>{capitalContent.examples.medium.description}</p>
                            </div>
                          )}
                          <div className="example-low">
                            <strong>{t('assessment_ui.example_low', 'BAJO')} ({capitalContent.examples.low.tokens} {t('assessment_ui.tokens', 'tokens')}):</strong>
                            <p>{capitalContent.examples.low.description}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <p className="question-text">{capitalContent.key_question}</p>
                </div>
              )}

              {/* Fallback if no content loaded */}
              {!capitalContent && (
                <div className="detail-fallback">
                  <div className="detail-definition">{currentCapital.definition}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Navigation Bar */}
      <div className="navigation">
        <Button onClick={() => currentTabIndex > 0 && setCurrentTabIndex(currentTabIndex - 1)} disabled={currentTabIndex === 0} variant="outline" size="sm">← {t('assessment_ui.previous', 'Anterior')}</Button>
        <Button onClick={clearAll} variant="destructive" size="sm">🔄 {t('assessment_ui.reset_all', 'Resetear')}</Button>
        <Button onClick={distributeEvenly} variant="outline" size="sm">{t('assessment_ui.distribute_equal', 'Distribuir Igual')}</Button>
        <Button onClick={goToNextEmpty} variant="outline" size="sm">{t('assessment_ui.next_empty', 'Siguiente Vacío')}</Button>
        <Button onClick={() => setShowSummary(true)} variant="outline" size="sm">{t('assessment_ui.view_summary', 'Ver Resumen')}</Button>
        {currentTabIndex < metaCapitals.length - 1 && (
          <Button onClick={() => setCurrentTabIndex(currentTabIndex + 1)} size="sm">{t('assessment_ui.next', 'Siguiente')} →</Button>
        )}
        <Button 
          onClick={handleSubmit} 
          disabled={!isValid()} 
          size="sm"
          style={{ 
            background: isValid() ? '#10b981' : '#d1d5db',
            color: 'white',
            fontWeight: '600'
          }}
        >
          {isValid() ? '✓ ' : ''}{t('assessment_ui.next', 'Continuar')} → {isOferta ? t('sections.needs', 'Necesidades') : t('sections.validation', 'Validación')}
        </Button>
      </div>
    </div>
  );
};

export default AssessmentTokenUI;
